import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Navbar, Footer } from "../../components/Layout";
import { toast, Toaster } from "sonner";
import { ArrowLeft, Plus, Edit, Trash2, X, Mail, Phone, Building, Copy, Check } from "lucide-react";

const emptyForm = {
  email: "", full_name: "", phone: "", password: "",
  address: "", postal_code: "", city: "", company: "", siret: "", notes: "", send_invite: true,
};

export default function TenantsAdmin() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // null or tenant id
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [showInvite, setShowInvite] = useState(null); // {email, password}
  const [copied, setCopied] = useState(false);

  const load = async () => {
    const r = await api.get("/admin/tenants");
    setItems(r.data);
  };
  useEffect(() => { load().catch(() => toast.error("Échec de chargement")); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const startNew = () => { setEditing("new"); setForm(emptyForm); };
  const startEdit = (t) => {
    setEditing(t.id);
    setForm({
      email: t.email, full_name: t.name, phone: t.phone || "", password: "",
      address: t.address || "", postal_code: t.postal_code || "", city: t.city || "",
      company: t.company || "", siret: t.siret || "", notes: t.notes || "", send_invite: false,
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editing === "new") {
        const payload = { ...form, password: form.password || null };
        const { data } = await api.post("/admin/tenants", payload);
        toast.success("Locataire créé");
        if (data._one_time_password) setShowInvite({ email: data.email, password: data._one_time_password });
        setEditing(null);
        load();
      } else {
        const upd = { full_name: form.full_name, phone: form.phone, address: form.address, postal_code: form.postal_code, city: form.city, company: form.company, siret: form.siret, notes: form.notes };
        if (form.password) upd.password = form.password;
        await api.put(`/admin/tenants/${editing}`, upd);
        toast.success("Locataire mis à jour");
        setEditing(null);
        load();
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Échec");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!confirm("Supprimer ce locataire et tous ses baux associés ?")) return;
    await api.delete(`/admin/tenants/${id}`);
    toast.success("Locataire supprimé");
    load();
  };

  const copyCreds = () => {
    if (!showInvite) return;
    navigator.clipboard.writeText(`Email: ${showInvite.email}\nMot de passe: ${showInvite.password}\nURL: ${window.location.origin}/locataire`);
    setCopied(true); setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--bg-default)" }}>
      <Navbar />
      <Toaster richColors position="top-right" />
      <section className="max-w-6xl mx-auto px-6 md:px-8 py-12">
        <button onClick={() => navigate("/admin")} className="text-xs font-bold uppercase tracking-widest mb-6 flex items-center gap-2" style={{ color: "var(--text-secondary)" }} data-testid="back-to-dashboard">
          <ArrowLeft className="w-3 h-3" /> Retour au tableau de bord
        </button>
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight">Locataires</h1>
            <p className="text-sm mt-2" style={{ color: "var(--text-secondary)" }}>Gérez les locataires et leurs accès à l'espace dédié.</p>
          </div>
          <button onClick={startNew} data-testid="new-tenant-btn"
            className="btn-brutal btn-brutal-solar px-5 py-3 font-bold uppercase tracking-wider text-sm flex items-center gap-2">
            <Plus className="w-4 h-4" /> Nouveau locataire
          </button>
        </div>

        <div className="bg-[color:var(--bg-surface)] border overflow-x-auto" style={{ borderColor: "var(--border-default)" }}>
          <table className="w-full text-sm">
            <thead className="bg-slate-900 text-white">
              <tr>
                <th className="px-4 py-3 text-left text-xs uppercase tracking-wider">Nom</th>
                <th className="px-4 py-3 text-left text-xs uppercase tracking-wider">Contact</th>
                <th className="px-4 py-3 text-left text-xs uppercase tracking-wider">Entreprise</th>
                <th className="px-4 py-3 text-left text-xs uppercase tracking-wider">Bail actif</th>
                <th className="px-4 py-3 text-right text-xs uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && <tr><td colSpan="5" className="p-12 text-center opacity-50 font-mono">Aucun locataire.</td></tr>}
              {items.map(t => (
                <tr key={t.id} className="border-t border-slate-900/10" data-testid={`row-tenant-${t.id}`}>
                  <td className="px-4 py-3 font-semibold">{t.name}</td>
                  <td className="px-4 py-3 text-xs">
                    <div className="flex items-center gap-2"><Mail className="w-3 h-3"/> <a href={`mailto:${t.email}`} className="underline">{t.email}</a></div>
                    {t.phone && <div className="flex items-center gap-2 mt-1 text-slate-500"><Phone className="w-3 h-3"/> {t.phone}</div>}
                  </td>
                  <td className="px-4 py-3 text-xs">{t.company || "—"}{t.siret && <div className="text-slate-500 font-mono">{t.siret}</div>}</td>
                  <td className="px-4 py-3 text-xs">{t.active_lease ? <span className="tag bg-emerald-100">Actif · {t.active_lease.rent_monthly} €</span> : <span className="opacity-50">Aucun</span>}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button onClick={() => startEdit(t)} data-testid={`edit-tenant-${t.id}`} className="p-2 border border-slate-900 hover:bg-slate-900 hover:text-white mr-2"><Edit className="w-4 h-4"/></button>
                    <button onClick={() => remove(t.id)} className="p-2 border border-red-300 text-red-600 hover:bg-red-50"><Trash2 className="w-4 h-4"/></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Edit modal */}
        {editing && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
            <div className="bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-slate-900" onClick={e => e.stopPropagation()} data-testid="tenant-modal">
              <div className="flex items-center justify-between p-6 border-b border-slate-900/10">
                <h2 className="font-display font-black text-2xl">{editing === "new" ? "Nouveau locataire" : "Modifier le locataire"}</h2>
                <button onClick={() => setEditing(null)} className="p-1"><X className="w-5 h-5"/></button>
              </div>
              <div className="p-6 space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <F label="Nom complet *"><input value={form.full_name} onChange={e => setForm({...form, full_name: e.target.value})} className="input" data-testid="tm-name" /></F>
                  <F label="Email *"><input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} disabled={editing !== "new"} className="input disabled:opacity-50" data-testid="tm-email" /></F>
                  <F label="Téléphone"><input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="input" data-testid="tm-phone" /></F>
                  <F label={editing === "new" ? "Mot de passe (vide = généré)" : "Nouveau mot de passe (vide = inchangé)"}><input type="text" value={form.password} onChange={e => setForm({...form, password: e.target.value})} className="input font-mono" data-testid="tm-password" /></F>
                  <F label="Société"><input value={form.company} onChange={e => setForm({...form, company: e.target.value})} className="input" /></F>
                  <F label="SIRET"><input value={form.siret} onChange={e => setForm({...form, siret: e.target.value})} className="input font-mono" maxLength="14" /></F>
                  <F label="Adresse" wide><input value={form.address} onChange={e => setForm({...form, address: e.target.value})} className="input" /></F>
                  <F label="Code postal"><input value={form.postal_code} onChange={e => setForm({...form, postal_code: e.target.value})} className="input" /></F>
                  <F label="Ville"><input value={form.city} onChange={e => setForm({...form, city: e.target.value})} className="input" /></F>
                </div>
                <F label="Notes internes"><textarea rows={3} value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} className="input resize-none" /></F>
                {editing === "new" && (
                  <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.send_invite} onChange={e => setForm({...form, send_invite: e.target.checked})} data-testid="tm-send-invite" /> Envoyer un email d'invitation avec les identifiants</label>
                )}
              </div>
              <div className="p-6 border-t border-slate-900/10 flex justify-end gap-3">
                <button onClick={() => setEditing(null)} className="btn-brutal bg-white border border-slate-900 px-4 py-2 text-sm font-bold uppercase tracking-wider">Annuler</button>
                <button onClick={save} disabled={saving || !form.full_name || !form.email} data-testid="tm-save"
                  className="btn-brutal btn-brutal-solar px-5 py-2 text-sm font-bold uppercase tracking-wider disabled:opacity-50">
                  {saving ? "..." : (editing === "new" ? "Créer" : "Enregistrer")}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* One-time password modal */}
        {showInvite && (
          <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
            <div className="bg-white max-w-md w-full border-4 border-[#FFC107] p-8" data-testid="invite-modal">
              <h2 className="font-display font-black text-xl mb-3">Identifiants du locataire</h2>
              <p className="text-sm text-slate-600 mb-4">Conservez ces identifiants : le mot de passe n'est plus consultable ensuite. L'email d'invitation a été {form.send_invite ? "envoyé" : "n'a pas été envoyé"}.</p>
              <div className="bg-slate-900 text-white p-4 font-mono text-sm space-y-2 mb-4">
                <div><span className="text-[#FFC107]">URL :</span> {window.location.origin}/locataire</div>
                <div><span className="text-[#FFC107]">Email :</span> {showInvite.email}</div>
                <div><span className="text-[#FFC107]">Mot de passe :</span> {showInvite.password}</div>
              </div>
              <div className="flex gap-3">
                <button onClick={copyCreds} className="btn-brutal bg-white border border-slate-900 px-4 py-2 text-sm font-bold uppercase tracking-wider flex-1 flex items-center justify-center gap-2">
                  {copied ? <Check className="w-4 h-4"/> : <Copy className="w-4 h-4"/>} {copied ? "Copié !" : "Copier"}
                </button>
                <button onClick={() => setShowInvite(null)} className="btn-brutal btn-brutal-solar px-4 py-2 text-sm font-bold uppercase tracking-wider flex-1">Fermer</button>
              </div>
            </div>
          </div>
        )}
      </section>
      <Footer />
    </div>
  );
}

const F = ({ label, children, wide }) => (
  <label className={`block ${wide ? "md:col-span-2" : ""}`}>
    <span className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2 block">{label}</span>
    {children}
  </label>
);
